import { useEffect, useMemo, useState } from "react";
import type { NavKey, PanelUser } from "../types";
import { openPhpMyAdmin } from "../lib/pma";
import {
  CPANEL_SECTIONS,
  loadCpanelFavorites,
  toggleCpanelFavorite,
  toolsForFeatures,
  type CpanelTool
} from "../cpanelTools";
import { hrefForNav } from "../lib/routes";
import type { WhmNavOpts } from "../components/Shell";

const SECTION_META: Record<
  string,
  { icon: string; color: string; label: string }
> = {
  mail: { icon: "✉", color: "#8B5CF6", label: "Email" },
  domains: { icon: "◎", color: "#2563EB", label: "Domain" },
  files: { icon: "📁", color: "#F59E0B", label: "Files" },
  databases: { icon: "⛁", color: "#10B981", label: "Database" },
  metrics: { icon: "📊", color: "#06B6D4", label: "Metrics" },
  security: { icon: "⛨", color: "#EF4444", label: "Security" },
  software: { icon: "✦", color: "#EC4899", label: "Applications" },
  advanced: { icon: "⚙", color: "#64748B", label: "Configuration" },
  preferences: { icon: "◐", color: "#6366F1", label: "Preferences" }
};

const TOOL_COLORS = [
  "#2563EB",
  "#8B5CF6",
  "#EC4899",
  "#F59E0B",
  "#10B981",
  "#06B6D4",
  "#EF4444",
  "#6366F1"
];

/**
 * Premium cPanel home — Webuzo-inspired layout, modern RealPanel design.
 */
export function CpanelHomePage({
  user,
  features,
  packageName,
  sitesCount,
  databasesCount = 0,
  serverIp,
  searchQuery = "",
  onSearchChange,
  onNav,
  onRefresh
}: {
  user: PanelUser;
  features: Record<string, boolean> | null;
  packageName?: string | null;
  sitesCount?: number;
  databasesCount?: number;
  serverIp?: string | null;
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
  onNav: (k: NavKey, opts?: WhmNavOpts) => void;
  onRefresh: () => Promise<void>;
}) {
  const tools = useMemo(() => toolsForFeatures(features), [features]);
  const q = searchQuery;
  const setQ = (v: string) => onSearchChange?.(v);
  const [favs, setFavs] = useState(() => loadCpanelFavorites());
  const [pmaError, setPmaError] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem("realpanel_cp_collapsed_v1");
      return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });

  const navOptsForTool = (t: CpanelTool): WhmNavOpts => {
    const opts: WhmNavOpts = { toolId: t.id };
    if (t.section === "ftp" || t.nav === "ftp") {
      opts.ftpTab =
        t.hint === "add"
          ? "add"
          : t.hint === "connections"
            ? "connections"
            : "manage";
    }
    return opts;
  };

  const openTool = (t: CpanelTool, e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    if (t.id === "phpmyadmin") {
      setPmaError("");
      void openPhpMyAdmin({ userId: user.id, serverWide: false }).catch(
        (err) => {
          setPmaError(err instanceof Error ? err.message : String(err));
          onNav("databases", { toolId: "phpmyadmin" });
        }
      );
      return;
    }
    // Install Apps / Application Manager / all other tools — pass toolId so
    // deep-link + correct page mode work (was broken: only nav key was sent).
    onNav(t.nav, navOptsForTool(t));
  };

  const toolHref = (t: CpanelTool): string => {
    if (t.id === "phpmyadmin") {
      return hrefForNav("databases", { toolId: "phpmyadmin" });
    }
    return hrefForNav(t.nav, navOptsForTool(t));
  };

  const favoriteTools = useMemo(() => {
    const byId = new Map(tools.map((t) => [t.id, t]));
    return favs
      .map((id) => byId.get(id))
      .filter((t): t is CpanelTool => Boolean(t));
  }, [favs, tools]);

  useEffect(() => {
    localStorage.setItem(
      "realpanel_cp_collapsed_v1",
      JSON.stringify(collapsed)
    );
  }, [collapsed]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return tools;
    return tools.filter(
      (t) =>
        t.label.toLowerCase().includes(s) ||
        t.description.toLowerCase().includes(s)
    );
  }, [tools, q]);

  const bySection = useMemo(() => {
    const map = new Map<string, CpanelTool[]>();
    for (const t of filtered) {
      const arr = map.get(t.section) || [];
      arr.push(t);
      map.set(t.section, arr);
    }
    return map;
  }, [filtered]);

  const sectionsWithTools = CPANEL_SECTIONS.filter(
    (s) => s.id !== "home" && (bySection.get(s.id) || []).length > 0
  );

  const diskQuota = user.quotaDiskMb || 0;
  const diskLabel =
    diskQuota > 0
      ? `${user.diskUsedMb} / ${diskQuota} MB`
      : `${user.diskUsedMb} MB / ∞`;
  const bandwidthLabel =
    user.limitsOverride?.bandwidthMb != null &&
    user.limitsOverride.bandwidthMb > 0
      ? `${user.limitsOverride.bandwidthMb} MB`
      : "unlimited";

  const searching = q.trim().length > 0;

  const kpis = [
    {
      icon: "👤",
      color: "#8B5CF6",
      label: "Current User",
      value: user.username,
      sub: packageName || user.role
    },
    {
      icon: "◎",
      color: "#0EA5E9",
      label: "Primary Domain",
      value: user.primaryDomain || "—",
      sub: "Main website"
    },
    {
      icon: "ℹ",
      color: "#10B981",
      label: "IP Address",
      value: serverIp || "—",
      sub: "Server IP"
    },
    {
      icon: "⌂",
      color: "#F43F5E",
      label: "Home Directory",
      value: user.homePath || "—",
      sub: "Document root"
    },
    {
      icon: "↩",
      color: "#14B8A6",
      label: "Last Login",
      value: user.lastLoginAt
        ? new Date(user.lastLoginAt).toLocaleString()
        : "—",
      sub: "Session history"
    },
    {
      icon: "▤",
      color: "#F59E0B",
      label: "Total Domains",
      value: String(sitesCount ?? 0),
      sub: "Sites on account"
    },
    {
      icon: "⛁",
      color: "#3B82F6",
      label: "Total Databases",
      value: String(databasesCount),
      sub: "MySQL databases"
    },
    {
      icon: "📈",
      color: "#6366F1",
      label: "Bandwidth",
      value: bandwidthLabel,
      sub: diskLabel
    }
  ];

  return (
    <div className="cp-home">
      {pmaError ? (
        <div className="error-banner" style={{ marginBottom: 12 }}>
          phpMyAdmin: {pmaError}
        </div>
      ) : null}
      <div className="cp-home-head">
        <div>
          <h1 className="cp-home-title">Home</h1>
          <p className="cp-home-sub">
            Welcome back, <strong>{user.username}</strong>
            {packageName ? (
              <>
                {" "}
                · <span className="mono">{packageName}</span>
              </>
            ) : null}
          </p>
        </div>
        <div className="cp-home-actions">
          <div className="cp-search-wrap">
            <span className="cp-search-icon" aria-hidden>
              ⌕
            </span>
            <input
              className="cp-search"
              placeholder="Search tools…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search cPanel tools"
            />
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => void onRefresh()}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* KPI grid — Webuzo style */}
      <div className="cp-kpi-grid">
        {kpis.map((k) => (
          <div key={k.label} className="cp-kpi-card">
            <span
              className="cp-kpi-icon"
              style={{ background: k.color }}
              aria-hidden
            >
              {k.icon}
            </span>
            <div className="cp-kpi-text">
              <span className="cp-kpi-label">{k.label}</span>
              <span className="cp-kpi-value" title={k.value}>
                {k.value}
              </span>
              <span className="cp-kpi-sub">{k.sub}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="cp-home-body">
        <div className="cp-home-main">
          {/* Favourites — starred tools always on top for quick access */}
          {!searching && favoriteTools.length > 0 ? (
            <section className="cp-section-card cp-fav-section">
              <div className="cp-section-hd static">
                <span
                  className="cp-section-badge"
                  style={{ background: "#FEF3C7", color: "#D97706" }}
                >
                  ★
                </span>
                <span className="cp-section-title">Favourites</span>
                <span className="cp-section-count">{favoriteTools.length}</span>
              </div>
              <p className="cp-fav-hint">
                Star any tool below to pin it here for one-click access.
              </p>
              <div className="cp-tool-grid">
                {favoriteTools.map((t, i) => (
                  <ToolIcon
                    key={`fav-${t.id}`}
                    tool={t}
                    color={TOOL_COLORS[i % TOOL_COLORS.length]!}
                    favorited
                    href={toolHref(t)}
                    onOpen={(e) => openTool(t, e)}
                    onToggleFav={() => setFavs(toggleCpanelFavorite(t.id))}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {searching ? (
            <section className="cp-section-card">
              <div className="cp-section-hd">
                <span className="cp-section-title">
                  Results for “{q.trim()}”
                </span>
                <span className="cp-section-count">{filtered.length}</span>
              </div>
              {filtered.length === 0 ? (
                <p className="cp-empty">No tools match your search.</p>
              ) : (
                <div className="cp-tool-grid">
                  {filtered.map((t, i) => (
                    <ToolIcon
                      key={t.id}
                      tool={t}
                      color={TOOL_COLORS[i % TOOL_COLORS.length]!}
                      favorited={favs.includes(t.id)}
                      href={toolHref(t)}
                      onOpen={(e) => openTool(t, e)}
                      onToggleFav={() =>
                        setFavs(toggleCpanelFavorite(t.id))
                      }
                    />
                  ))}
                </div>
              )}
            </section>
          ) : (
            sectionsWithTools.map((s) => {
              const items = bySection.get(s.id) || [];
              const meta = SECTION_META[s.id] || {
                icon: "•",
                color: "#64748B",
                label: s.label
              };
              const isOpen = !collapsed[s.id];
              return (
                <section key={s.id} className="cp-section-card">
                  <button
                    type="button"
                    className="cp-section-hd"
                    onClick={() =>
                      setCollapsed((c) => ({ ...c, [s.id]: !c[s.id] }))
                    }
                    aria-expanded={isOpen}
                  >
                    <span
                      className="cp-section-badge"
                      style={{ background: `${meta.color}18`, color: meta.color }}
                    >
                      {meta.icon}
                    </span>
                    <span className="cp-section-title">{meta.label}</span>
                    <span className="cp-section-count">{items.length}</span>
                    <span
                      className={`cp-section-chevron ${isOpen ? "open" : ""}`}
                      aria-hidden
                    >
                      ⌃
                    </span>
                  </button>
                  {isOpen ? (
                    <div className="cp-tool-grid">
                      {items.map((t, i) => (
                        <ToolIcon
                          key={t.id}
                          tool={t}
                          color={
                            TOOL_COLORS[(i + s.id.length) % TOOL_COLORS.length]!
                          }
                          favorited={favs.includes(t.id)}
                          href={toolHref(t)}
                          onOpen={(e) => openTool(t, e)}
                          onToggleFav={() =>
                            setFavs(toggleCpanelFavorite(t.id))
                          }
                        />
                      ))}
                    </div>
                  ) : null}
                </section>
              );
            })
          )}
        </div>

        <aside className="cp-home-side">
          <section className="cp-section-card cp-side-card">
            <div className="cp-section-hd static">
              <span className="cp-section-title">Usage</span>
            </div>
            <div className="cp-usage-block">
              <div className="cp-usage-row">
                <span>Disk</span>
                <strong>
                  {user.diskUsedMb} MB
                  {diskQuota > 0 ? ` / ${diskQuota} MB` : " / ∞"}
                </strong>
              </div>
              <div className="cp-usage-bar">
                <div
                  className="cp-usage-fill"
                  style={{
                    width: `${
                      diskQuota > 0
                        ? Math.min(
                            100,
                            Math.round((user.diskUsedMb / diskQuota) * 100)
                          )
                        : 8
                    }%`
                  }}
                />
              </div>
              <div className="cp-usage-row">
                <span>Bandwidth</span>
                <strong>{bandwidthLabel}</strong>
              </div>
              <div className="cp-usage-bar">
                <div className="cp-usage-fill soft" style={{ width: "12%" }} />
              </div>
              <div className="cp-usage-row">
                <span>Status</span>
                <strong
                  className={
                    user.status === "active" ? "cp-ok" : "cp-warn"
                  }
                >
                  {user.status === "active" ? "Active" : user.status}
                </strong>
              </div>
            </div>
          </section>

          <section className="cp-section-card cp-side-card">
            <div className="cp-section-hd static">
              <span className="cp-section-title">Quick actions</span>
            </div>
            <div className="cp-quick-list">
              {[
                {
                  label: "File Manager",
                  nav: "files" as NavKey,
                  toolId: "file-manager",
                  show: true
                },
                {
                  label: "Email Accounts",
                  nav: "email" as NavKey,
                  toolId: "email-accounts",
                  show: true
                },
                {
                  label: "Databases",
                  nav: "databases" as NavKey,
                  toolId: "mysql",
                  show: true
                },
                {
                  label: "Install Apps",
                  nav: "apps" as NavKey,
                  toolId: "apps",
                  show: tools.some((t) => t.nav === "apps")
                },
                {
                  label: "SSL/TLS",
                  nav: "ssl" as NavKey,
                  toolId: "auto-ssl",
                  show: true
                }
              ]
                .filter((x) => x.show)
                .map((x) => (
                  <a
                    key={x.nav + x.label}
                    href={hrefForNav(x.nav, { toolId: x.toolId })}
                    className="cp-quick-item"
                    onClick={(e) => {
                      e.preventDefault();
                      onNav(x.nav, { toolId: x.toolId });
                    }}
                  >
                    {x.label}
                    <span aria-hidden>→</span>
                  </a>
                ))}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}

function ToolIcon({
  tool,
  color,
  favorited,
  href,
  onOpen,
  onToggleFav
}: {
  tool: CpanelTool;
  color: string;
  favorited: boolean;
  href?: string;
  onOpen: (e?: { preventDefault: () => void }) => void;
  onToggleFav: () => void;
}) {
  return (
    <div className="cp-tool">
      <a
        href={href || "#"}
        className="cp-tool-btn"
        onClick={(e) => onOpen(e)}
      >
        <span
          className="cp-tool-icon"
          style={{
            background: `${color}14`,
            color,
            borderColor: `${color}33`
          }}
          aria-hidden
        >
          {tool.icon}
        </span>
        <span className="cp-tool-label">{tool.label}</span>
      </a>
      <button
        type="button"
        className={`cp-tool-star ${favorited ? "on" : ""}`}
        title={favorited ? "Remove from Favourites" : "Add to Favourites"}
        aria-label={
          favorited
            ? `Remove ${tool.label} from favourites`
            : `Add ${tool.label} to favourites`
        }
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleFav();
        }}
      >
        {favorited ? "★" : "☆"}
      </button>
    </div>
  );
}
